/**
 * Стандартні редактори (2026-08-29, продовження AssetDocEditors): внутрішнє
 * переміщення, інвентаризація ОЗ, «Зміна стану» (хаб: модернізація /
 * консервація / ремонт / повернення / зміна строку). Канон той самий:
 * DocumentToolbar (дт-кт, друк), історія, вкладення, чернетки, .tch.
 *
 * ч.915: усі чотири форми переведено на новий дизайн сторінок документів
 * (Закон дизайну #10): DocLayout / DocBlock / DocRail — як у AssetDocEditors.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { depreciationMethods, fixedAssets, intangibleAssets, lowValueAssets, type AssetKind, type AssetStatus, type FixedAsset, type LowValueAsset } from '@/api/assets';
import { internalTransfer, modernization, type InternalTransferItem, type ModernizationItem } from '@/api/asset_movement';
import { inventory as inventoryApi, type InventoryItem, type ActualStatus, type LocationKind } from '@/api/asset_inventory_sale';
import { assetStateChange, CHANGE_KIND_UA, type ChangeKind } from '@/api/assetStateChange';
import { ASSET_KIND_UA, ASSET_STATUS_UA } from '@/api/assetsRegistry';
import { departmentsApi, employees as employeesApi } from '@/api/hr';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import AssetPickerModal from './AssetPickerModal';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const errDetail = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
};
const today = () => new Date().toISOString().slice(0, 10);
type AnyAsset = (FixedAsset | LowValueAsset) & { residual_value?: string; useful_life_months?: number; accumulated_depreciation?: string };
type Cand = { kind: AssetKind; a: AnyAsset };

function useAssetCandidates(orgId: string, wanted: AssetStatus[], onlyDepr = false) {
  const faQ = useQuery({ queryKey: ['fixed-assets-v2', orgId], queryFn: () => fixedAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const iaQ = useQuery({ queryKey: ['intangible-assets-v2', orgId], queryFn: () => intangibleAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const lvQ = useQuery({ queryKey: ['low-value-assets', orgId], queryFn: () => lowValueAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId && !onlyDepr });
  const all = useMemo<Cand[]>(() => [
    ...(faQ.data?.items ?? []).map((a) => ({ kind: 'FIXED_ASSET' as AssetKind, a: a as AnyAsset })),
    ...(iaQ.data?.items ?? []).map((a) => ({ kind: 'INTANGIBLE' as AssetKind, a: a as AnyAsset })),
    ...(lvQ.data?.items ?? []).map((a) => ({ kind: 'LOW_VALUE' as AssetKind, a: a as AnyAsset })),
  ], [faQ.data, iaQ.data, lvQ.data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.a.id, c])), [all]);
  return { all, byId, candidates: all.filter(({ a }) => wanted.includes(a.status)) };
}
function useDeptsEmps(orgId: string) {
  // Окремі ключі кешу (2026-08-29): ['departments', org] / ['employees-all', org]
  // інші сторінки зберігають як Page {items}, тут — масив → колізія кешу
  // давала «deps.map is not a function» при переході з іншої сторінки.
  const depsQ = useQuery({ queryKey: ['departments-items', orgId], queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 500 }).then((r) => r.items), enabled: !!orgId });
  const empQ = useQuery({ queryKey: ['employees-items', orgId], queryFn: () => employeesApi.list({ organization_id: orgId, limit: 500 }).then((r) => r.items), enabled: !!orgId });
  const asArr = <T,>(v: unknown): T[] => Array.isArray(v) ? v as T[] : ((v as { items?: T[] } | undefined)?.items ?? []);
  return { deps: asArr<{ id: string; name: string }>(depsQ.data), emps: asArr<{ id: string; full_name: string | null }>(empQ.data) };
}
function ImportedBanner({ source }: { source?: string | null }) {
  if (source !== '1c') return null;
  return (
    <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)', borderRadius: 6, padding: '0.4rem 0.75rem', margin: '0.5rem 0', fontSize: '0.85rem' }}>
      <ImportedBadge source="1c" /> Документ імпортовано з 1С — стан картки вже врахований. Проведення вимкнено.
    </div>
  );
}
// ч.915 (новий дизайн, Закон #10): блокування форми — fieldset у КОЖНОМУ блоці
// (канон ВМД): шапка блоку з діями лишається поза fieldset.
const fs = { border: 'none', margin: 0, padding: 0, minWidth: 0 } as const;
type RailAction = { label: string; title?: string; disabled?: boolean; onClick: () => void };

// =====================================================================
// Внутрішнє переміщення
// =====================================================================
export function InternalTransferEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate(); const qc = useQueryClient(); const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.asset_internal_transfer.create');
  const docQ = useQuery({ queryKey: ['asset-transfer', id], queryFn: () => internalTransfer.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({ organization_id: sp.get('organization_id') ?? '', document_date: today(), transfer_reason: '', comment: '', rows: [] as InternalTransferItem[] });
  useEffect(() => { if (d) setForm({ organization_id: d.organization_id, document_date: d.document_date.slice(0, 10), transfer_reason: d.transfer_reason, comment: d.comment ?? '', rows: d.items }); }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted; const imported = d?.external_source === '1c'; const locked = isPosted || imported;
  const { byId, candidates } = useAssetCandidates(orgEff, ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG']);
  const { deps, emps } = useDeptsEmps(orgEff);
  const used = new Set(form.rows.map((r) => r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id ?? ''));
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const aid = sp.get('asset_id');
    if (!isNew || !aid || prefilled) return;
    const c = candidates.find((x) => x.a.id === aid);
    if (c) { addRow(c); setPrefilled(true); }
  });
  function addRow(c: Cand) {
    setForm((f) => ({ ...f, rows: [...f.rows, {
      line_number: f.rows.length + 1, asset_kind: c.kind,
      fixed_asset_id: c.kind === 'FIXED_ASSET' ? c.a.id : null, intangible_asset_id: c.kind === 'INTANGIBLE' ? c.a.id : null,
      low_value_asset_id: c.kind === 'LOW_VALUE' ? c.a.id : null,
      from_department_id: c.a.department_id ?? null, to_department_id: null,
      from_employee_id: (c.a as { responsible_employee_id?: string | null }).responsible_employee_id ?? null, to_employee_id: null, item_reason: null,
    }] }));
  }
  const upd = (i: number, patch: Partial<InternalTransferItem>) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
  const payload = () => ({ organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`,
    transfer_reason: form.transfer_reason, comment: form.comment || null, items: form.rows.map((r, i) => ({ ...r, id: undefined, line_number: i + 1 })) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['asset-transfer'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => { const doc = isNew ? await internalTransfer.create(payload() as never) : await internalTransfer.update(id!, payload() as never); if (andPost) {
        try { await internalTransfer.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/transfer/${doc.id}`, { replace: true });
          throw e;
        }
      } return doc; },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/transfer/${doc.id}`, { replace: true }); }, onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => internalTransfer.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => internalTransfer.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/transfer'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && form.transfer_reason.trim().length >= 3 && form.rows.length > 0
    && form.rows.every((r) => r.to_department_id || r.to_employee_id);
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0)
        + (form.transfer_reason.trim().length >= 3 ? 1 : 0) },
    // ТЧ «заповнена» — як у canSave: є рядки І в кожному вказано, куди переміщуємо
    // (підрозділ або МВО). Новий рядок приходить БЕЗ призначення, тож лише
    // rows.length > 0 показувало б «готово до проведення» при вимкненому «провести».
    { id: 'items', title: 'Активи', required: 1,
      filled: form.rows.length > 0 && form.rows.every((r) => r.to_department_id || r.to_employee_id) ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{isNew ? 'Нове внутрішнє переміщення' : `Внутрішнє переміщення ${d?.document_number ?? ''}`}{imported && <ImportedBadge source="1c" />}</h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar onBackToList={() => navigate('/assets/transfer')} onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew} onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending} deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }} deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/transfer/${id}/print`)} entriesDoc={{ type: 'doc.asset_internal_transfer', id: isNew ? '' : id! }}
          railMode extraActions={railActions} />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.asset_internal_transfer', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={fs}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Організація *</label><select value={orgEff} disabled={!isNew} onChange={(e) => setForm({ ...form, organization_id: e.target.value, rows: [] })}>{availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата *</label><DatePopover value={form.document_date} onChange={(v) => setForm({ ...form, document_date: v })} /></div>
          <div className="field"><label>Підстава переміщення <span style={{ gridColumn: 'span 6', color: 'red' }}>*</span></label><input value={form.transfer_reason} onChange={(e) => setForm({ ...form, transfer_reason: e.target.value })} /></div>
        </div>
      </fieldset>
      </DocBlock>

      {/* ч.915: кнопка підбору — у шапці блоку ТЧ (DocBlock actions), поза fieldset. */}
      <DocBlock index={2} title="Активи"
        actions={!locked ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}>+ додати активи</button>
        ) : undefined}>
      <fieldset disabled={locked} style={fs}>
        <TchTable>
          <thead><tr><th>№</th><th>Актив</th><th>З підрозділу</th><th>У підрозділ</th><th>Від МВО</th><th>До МВО</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--muted)' }}>Додайте активи.</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id ?? '');
              const dep = (v?: string | null) => deps.find((x) => x.id === v)?.name ?? '—';
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{c ? `${ASSET_KIND_UA[c.kind]} ${c.a.inventory_number} ${c.a.name}` : '…'}</td>
                  <td style={{ color: 'var(--muted)' }}>{dep(r.from_department_id)}</td>
                  <td><select value={r.to_department_id ?? ''} onChange={(e) => upd(i, { to_department_id: e.target.value || null })}><option value="">—</option>{deps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></td>
                  <td style={{ color: 'var(--muted)' }}>{emps.find((x) => x.id === r.from_employee_id)?.full_name ?? '—'}</td>
                  <td><select value={r.to_employee_id ?? ''} onChange={(e) => upd(i, { to_employee_id: e.target.value || null })}><option value="">—</option>{emps.map((x) => <option key={x.id} value={x.id}>{x.full_name ?? x.id}</option>)}</select></td>
                  <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      </fieldset>
      {/* ч.915: модалка підбору — поза fieldset (відкривається кнопкою з шапки блоку). */}
      {pickerOpen && (
        <AssetPickerModal orgId={orgEff} candidates={candidates} used={used}
          onAdd={(list) => list.forEach(addRow)} onClose={() => setPickerOpen(false)} />
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={fs}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label><input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.asset_internal_transfer"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}

// =====================================================================
// Інвентаризація ОЗ
// =====================================================================
const ACT_UA: Record<ActualStatus, string> = { FOUND: 'в наявності', MISSING: 'відсутнє', FOUND_WRONG_LOCATION: 'не за місцем' };

export function InventoryEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate(); const qc = useQueryClient(); const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.fixed_asset_inventory.create');
  const docQ = useQuery({ queryKey: ['fa-inventory', id], queryFn: () => inventoryApi.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(), location_kind: 'DEPARTMENT' as LocationKind,
    department_id: '', employee_id: '', inventory_period_start: today(), inventory_period_end: today(),
    inventory_reason: '', commission_members_text: '', findings_summary: '', comment: '', rows: [] as InventoryItem[],
  });
  useEffect(() => { if (d) setForm({
    organization_id: d.organization_id, document_date: d.document_date.slice(0, 10), location_kind: d.location_kind,
    department_id: d.department_id ?? '', employee_id: d.employee_id ?? '', inventory_period_start: d.inventory_period_start,
    inventory_period_end: d.inventory_period_end, inventory_reason: d.inventory_reason ?? '', commission_members_text: d.commission_members_text ?? '',
    findings_summary: d.findings_summary ?? '', comment: d.comment ?? '', rows: d.items,
  }); }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted; const imported = d?.external_source === '1c'; const locked = isPosted || imported;
  const { byId } = useAssetCandidates(orgEff, []);
  const { deps, emps } = useDeptsEmps(orgEff);
  const [error, setError] = useState<string | null>(null);
  const fill = useMutation({
    mutationFn: () => inventoryApi.expectedItems({ organization_id: orgEff, location_kind: form.location_kind,
      department_id: form.location_kind === 'DEPARTMENT' ? form.department_id : undefined,
      employee_id: form.location_kind === 'EMPLOYEE' ? form.employee_id : undefined }),
    onSuccess: (res) => setForm({ ...form, rows: res.items.map((it, i) => ({
      line_number: i + 1, asset_kind: it.asset_kind,
      fixed_asset_id: it.asset_kind === 'FIXED_ASSET' ? it.asset_id : null, intangible_asset_id: it.asset_kind === 'INTANGIBLE' ? it.asset_id : null,
      low_value_asset_id: it.asset_kind === 'LOW_VALUE' ? it.asset_id : null,
      expected_department_id: it.expected_department_id, expected_employee_id: it.expected_employee_id,
      actual_status: 'FOUND' as ActualStatus, actual_department_id: it.expected_department_id, actual_employee_id: it.expected_employee_id,
      primary_cost_snapshot: String(it.primary_cost), accumulated_depreciation_snapshot: String(it.accumulated_depreciation), book_value_snapshot: String(it.book_value), item_note: null,
    })) }),
    onError: (e) => setError(errDetail(e)),
  });
  const upd = (i: number, patch: Partial<InventoryItem>) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`, location_kind: form.location_kind,
    department_id: form.location_kind === 'DEPARTMENT' ? form.department_id : null, employee_id: form.location_kind === 'EMPLOYEE' ? form.employee_id : null,
    inventory_period_start: form.inventory_period_start, inventory_period_end: form.inventory_period_end,
    inventory_reason: form.inventory_reason || null, commission_members_text: form.commission_members_text || null,
    findings_summary: form.findings_summary || null, comment: form.comment || null,
    items: form.rows.map((r, i) => ({
      line_number: i + 1, asset_kind: r.asset_kind, fixed_asset_id: r.fixed_asset_id ?? null, intangible_asset_id: r.intangible_asset_id ?? null,
      low_value_asset_id: r.low_value_asset_id ?? null, expected_department_id: r.expected_department_id ?? null, expected_employee_id: r.expected_employee_id ?? null,
      actual_status: r.actual_status,
      // || (не ??): порожній рядок '' зі стейту форми (скоуп ORGANIZATION)
      // мусить стати null — інакше 422 «Input should be a valid UUID».
      actual_department_id: r.actual_status === 'MISSING' ? null : (r.actual_department_id || r.expected_department_id || form.department_id || null),
      actual_employee_id: r.actual_status === 'MISSING' ? null : (r.actual_employee_id || r.expected_employee_id || null), item_note: r.item_note ?? null,
    })),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['fa-inventory'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => { const doc = isNew ? await inventoryApi.create(payload() as never) : await inventoryApi.update(id!, payload() as never); if (andPost) {
        try { await inventoryApi.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/inventory/${doc.id}`, { replace: true });
          throw e;
        }
      } return doc; },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/inventory/${doc.id}`, { replace: true }); }, onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => inventoryApi.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => inventoryApi.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/inventory'); }, onError: (e) => setError(errDetail(e)) });
  const locOk = form.location_kind === 'DEPARTMENT' ? !!form.department_id
    : form.location_kind === 'EMPLOYEE' ? !!form.employee_id : true;
  const canSave = !!orgEff && locOk && form.rows.length > 0;
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  // Місце проведення обовʼязкове, крім охоплення «по організації» (див. locOk).
  const locRequired = form.location_kind !== 'ORGANIZATION';
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: locRequired ? 3 : 2,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) + (locRequired && locOk ? 1 : 0) },
    { id: 'commission', title: 'Комісія і підстава', required: 0, filled: 0 },
    { id: 'items', title: 'Активи', required: 1, filled: form.rows.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{isNew ? 'Нова інвентаризація ОЗ' : `Інвентаризація ОЗ ${d?.document_number ?? ''}`}{imported && <ImportedBadge source="1c" />}
        </h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar onBackToList={() => navigate('/assets/inventory')} onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew} onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          postTitle="Проведення фіксує результати (нестачі → списання окремим документом)"
          extraStatusBadges={!isPosted && d?.state === 'IN_PROGRESS' ? [{
            label: 'в процесі', tone: 'amber',
            title: 'Опис у комісії — триває перерахунок. Внесіть фактичну наявність з підписаного опису і проведіть.',
          }] : []}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending} deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }} deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/inventory/${id}/print`)} entriesDoc={{ type: 'doc.fixed_asset_inventory', id: isNew ? '' : id! }}
          railMode extraActions={railActions} />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.fixed_asset_inventory', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={fs}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Організація *</label><select value={orgEff} disabled={!isNew} onChange={(e) => setForm({ ...form, organization_id: e.target.value, rows: [], department_id: '', employee_id: '' })}>{availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div className="field" style={{ gridColumn: 'span 2' }}><label>Дата *</label><DatePopover value={form.document_date} onChange={(v) => setForm({ ...form, document_date: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Де проводиться</label><select value={form.location_kind} onChange={(e) => setForm({ ...form, location_kind: e.target.value as LocationKind })}><option value="DEPARTMENT">у підрозділі</option><option value="EMPLOYEE">у МВО</option><option value="ORGANIZATION">по організації (всі активи)</option></select></div>
          {form.location_kind === 'DEPARTMENT'
            ? <div className="field" style={{ gridColumn: 'span 3' }}><label>Підрозділ <span style={{ color: 'red' }}>*</span></label><select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}><option value="">—</option>{deps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></div>
            : form.location_kind === 'EMPLOYEE'
            ? <div className="field" style={{ gridColumn: 'span 3' }}><label>МВО <span style={{ color: 'red' }}>*</span></label><select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}><option value="">—</option>{emps.map((x) => <option key={x.id} value={x.id}>{x.full_name ?? x.id}</option>)}</select></div>
            : <div className="field" style={{ gridColumn: 'span 3' }}><label>Охоплення</label><input disabled value="всі активи організації" title="Інвентаризація по всій організації — підрозділ/МВО не обираються" /></div>}
        </div>
      </fieldset>
      </DocBlock>

      <DocBlock index={2} title="Комісія і підстава">
      <fieldset disabled={locked} style={fs}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Період з</label><DatePopover value={form.inventory_period_start} onChange={(v) => setForm({ ...form, inventory_period_start: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>по</label><DatePopover value={form.inventory_period_end} onChange={(v) => setForm({ ...form, inventory_period_end: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Підстава</label><input value={form.inventory_reason} onChange={(e) => setForm({ ...form, inventory_reason: e.target.value })} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Комісія</label><input value={form.commission_members_text} onChange={(e) => setForm({ ...form, commission_members_text: e.target.value })} /></div>
        </div>
      </fieldset>
      </DocBlock>

      {/* ч.915: заповнення ТЧ — у шапці блоку (DocBlock actions, btn-doc), поза fieldset. */}
      <DocBlock index={3} title="Активи"
        actions={!locked ? (
          <button type="button" className="btn-doc" disabled={!locOk || fill.isPending} onClick={() => fill.mutate()}>заповнити за даними обліку</button>
        ) : undefined}>
      <fieldset disabled={locked} style={fs}>
        <TchTable>
          <thead><tr><th>№</th><th>Актив</th><th style={{ textAlign: 'right' }}>Первісна</th><th style={{ textAlign: 'right' }}>Залишкова</th><th>Фактично</th><th>Примітка</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--muted)' }}>Оберіть місце і натисніть «заповнити за даними обліку».</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id ?? '');
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{c ? `${c.a.inventory_number} ${c.a.name}` : '…'}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(r.primary_cost_snapshot)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(r.book_value_snapshot)}</td>
                  <td><select value={r.actual_status} onChange={(e) => upd(i, { actual_status: e.target.value as ActualStatus })}>{(Object.keys(ACT_UA) as ActualStatus[]).map((k) => <option key={k} value={k}>{ACT_UA[k]}</option>)}</select></td>
                  <td><input value={r.item_note ?? ''} onChange={(e) => upd(i, { item_note: e.target.value || null })} /></td>
                  <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
        {/* ч.915: висновки комісії лишаються ПІД ТЧ — їх вносять після фактичної
            наявності (той самий порядок, що й у друкованому описі). */}
        <div className="field" style={{ marginTop: '0.75rem' }}><label>Висновки комісії</label><input value={form.findings_summary} onChange={(e) => setForm({ ...form, findings_summary: e.target.value })} /></div>
      </fieldset>
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={fs}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label><input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.fixed_asset_inventory"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}

// =====================================================================
// «Зміна стану» — хаб: модернізація (свій документ) + консервація/ремонт/
// повернення/строк (doc.asset_state_change). Маршрути:
//   /assets/state-change/new?kind=…   /assets/state-change/m/:id   /assets/state-change/s/:id
// =====================================================================
type HubKind = 'MODERNIZATION' | ChangeKind;
const HUB_KIND_UA: Record<HubKind, string> = { MODERNIZATION: 'модернізація', ...CHANGE_KIND_UA };

export function StateChangeEditor({ docType }: { docType?: 'm' | 's' }) {
  const [sp] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const initialKind = (sp.get('kind') as HubKind) || (docType === 'm' ? 'MODERNIZATION' : 'CONSERVATION');
  const [kind, setKind] = useState<HubKind>(isNew ? initialKind : (docType === 'm' ? 'MODERNIZATION' : 'CONSERVATION'));
  const effectiveKind: HubKind = isNew ? kind : (docType === 'm' ? 'MODERNIZATION' : kind);
  return effectiveKind === 'MODERNIZATION'
    ? <ModernizationForm kind={kind} setKind={setKind} />
    : <StateChangeForm kind={kind as ChangeKind} setKind={setKind} />;
}

function KindSelect({ kind, setKind, disabled }: { kind: HubKind; setKind: (k: HubKind) => void; disabled: boolean }) {
  return (
    <div className="field" style={{ gridColumn: 'span 3' }}><label>Вид операції</label>
      <select value={kind} disabled={disabled} onChange={(e) => setKind(e.target.value as HubKind)}>
        {(Object.keys(HUB_KIND_UA) as HubKind[]).map((k) => <option key={k} value={k}>{HUB_KIND_UA[k]}</option>)}
      </select></div>
  );
}

function ModernizationForm({ kind, setKind }: { kind: HubKind; setKind: (k: HubKind) => void }) {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate(); const qc = useQueryClient(); const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.fixed_asset_modernization.create');
  const docQ = useQuery({ queryKey: ['fa-modernization', id], queryFn: () => modernization.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({ organization_id: sp.get('organization_id') ?? '', document_date: today(), fixed_asset_id: sp.get('asset_id') ?? '', comment: '', rows: [] as ModernizationItem[] });
  useEffect(() => { if (d) setForm({ organization_id: d.organization_id, document_date: d.document_date.slice(0, 10), fixed_asset_id: d.fixed_asset_id, comment: d.comment ?? '', rows: d.items }); }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted; const imported = d?.external_source === '1c'; const locked = isPosted || imported;
  const { candidates } = useAssetCandidates(orgEff, ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG'], true);
  const [error, setError] = useState<string | null>(null);
  const total = form.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const valid = form.rows.filter((r) => r.description.trim().length >= 3 && Number(r.amount) > 0);
  const upd = (i: number, patch: Partial<ModernizationItem>) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
  const payload = () => ({ organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`, fixed_asset_id: form.fixed_asset_id, comment: form.comment || null,
    items: valid.map((r, i) => ({ line_number: i + 1, description: r.description.trim(), amount: r.amount, cost_account_code: r.cost_account_code || '631' })) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['fa-modernization'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => { const doc = isNew ? await modernization.create(payload() as never) : await modernization.update(id!, payload() as never); if (andPost) {
        try { await modernization.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/state-change/m/${doc.id}`, { replace: true });
          throw e;
        }
      } return doc; },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/state-change/m/${doc.id}`, { replace: true }); }, onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => modernization.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => modernization.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/state-change'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && !!form.fixed_asset_id && valid.length > 0;
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) + (form.fixed_asset_id ? 1 : 0) },
    // ТЧ «заповнена» = є хоча б один ВАЛІДНИЙ рядок (опис ≥ 3 символів і сума > 0) —
    // саме їх рахує canSave і саме вони йдуть у payload; порожній «+ рядок» не рахується.
    { id: 'items', title: 'Витрати', required: 1, filled: valid.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{isNew ? 'Нова модернізація ОЗ' : `Модернізація ОЗ ${d?.document_number ?? ''}`}{imported && <ImportedBadge source="1c" />}</h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar onBackToList={() => navigate('/assets/state-change')} onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew} onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending} deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }} deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/state-change/m/${id}/print`)} entriesDoc={{ type: 'doc.fixed_asset_modernization', id: isNew ? '' : id! }}
          railMode extraActions={railActions} />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.fixed_asset_modernization', id: isNew ? '' : id! }} />
      }>
      {/* ч.915: «Вид операції» (перемикач форм хаба) лишається в «Основних даних» —
          там само, де й у формі зміни стану. */}
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={fs}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Організація *</label><select value={orgEff} disabled={!isNew} onChange={(e) => setForm({ ...form, organization_id: e.target.value, fixed_asset_id: '' })}>{availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div className="field" style={{ gridColumn: 'span 2' }}><label>Дата *</label><DatePopover value={form.document_date} onChange={(v) => setForm({ ...form, document_date: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 4' }}><label>Основний засіб <span style={{ color: 'red' }}>*</span></label>
            {isNew ? (
              <SearchSelect
                items={candidates.filter((c) => c.kind === 'FIXED_ASSET')
                  .map((c) => ({ id: c.a.id, label: c.a.name, hint: c.a.inventory_number }))}
                value={form.fixed_asset_id}
                onChange={(v) => setForm({ ...form, fixed_asset_id: v })}
                placeholder="пошук: назва або інв. №"
              />
            ) : (
              <input disabled value={(candidates.find((c) => c.a.id === form.fixed_asset_id)?.a.name) ?? ''} />
            )}
          </div>
          <KindSelect kind={kind} setKind={setKind} disabled={!isNew} />
        </div>
      </fieldset>
      </DocBlock>

      {/* ч.915: додавання рядка — у шапці блоку ТЧ (DocBlock actions), поза fieldset. */}
      <DocBlock index={2} title="Витрати"
        actions={!locked ? (
          <button type="button" className="primary" onClick={() => setForm({ ...form, rows: [...form.rows, { line_number: form.rows.length + 1, description: '', amount: '', cost_account_code: '631' }] })}>+ рядок витрат</button>
        ) : undefined}>
      <fieldset disabled={locked} style={fs}>
        <TchTable>
          <thead><tr><th>№</th><th>Витрати (опис)</th><th style={{ textAlign: 'right' }}>Сума</th><th>Рах. витрат</th><th /></tr></thead>
          <tbody>
            {form.rows.map((r, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td><input value={r.description} onChange={(e) => upd(i, { description: e.target.value })} style={{ width: '100%' }} /></td>
                <td><input type="number" step="0.01" value={r.amount} style={{ textAlign: 'right', width: 130 }} onChange={(e) => upd(i, { amount: e.target.value })} /></td>
                <td><input value={r.cost_account_code ?? '631'} style={{ width: 80 }} onChange={(e) => upd(i, { cost_account_code: e.target.value })} /></td>
                <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={2}>Разом капіталізується</td><td style={{ textAlign: 'right' }}>{fmt2(total)}</td><td colSpan={2} /></tr></tfoot>
        </TchTable>
      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={fs}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label><input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.fixed_asset_modernization"
            documentId={isNew ? undefined : id} />
          {/* ч.915: підказка нового документа — поруч із вкладеннями (текст без змін). */}
          {isNew && <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.4rem 0 0' }}>Вкладення зʼявляться після збереження документа.</p>}
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}

function StateChangeForm({ kind, setKind }: { kind: ChangeKind; setKind: (k: HubKind) => void }) {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate(); const qc = useQueryClient(); const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.fixed_asset_modernization.create');
  const docQ = useQuery({ queryKey: ['asset-state-change', id], queryFn: () => assetStateChange.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(), effective_date: today(),
    asset_kind: ((sp.get('kind_asset') as 'FIXED_ASSET' | 'INTANGIBLE') || 'FIXED_ASSET'), asset_id: sp.get('asset_id') ?? '',
    reason: '', new_useful_life_months: '', new_depreciation_method_id: '', new_liquidation_value: '', comment: '',
  });
  useEffect(() => { if (d) { setKind(d.change_kind); setForm({
    organization_id: d.organization_id, document_date: d.document_date.slice(0, 10), effective_date: d.effective_date,
    asset_kind: d.asset_kind, asset_id: d.fixed_asset_id ?? d.intangible_asset_id ?? '', reason: d.reason ?? '',
    new_useful_life_months: d.new_useful_life_months != null ? String(d.new_useful_life_months) : '',
    new_depreciation_method_id: d.new_depreciation_method_id ?? '', new_liquidation_value: d.new_liquidation_value != null ? String(d.new_liquidation_value) : '', comment: d.comment ?? '',
  }); } }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted; const imported = d?.external_source === '1c'; const locked = isPosted || imported;
  const wanted: AssetStatus[] = kind === 'OPERATING' ? ['CONSERVATION', 'REPAIR_LONG'] : (kind === 'USEFUL_LIFE' || kind === 'DEPR_PARAMS') ? ['OPERATING', 'CONSERVATION', 'REPAIR_LONG', 'PARKED'] : ['OPERATING'];
  const methodsQ2 = useQuery({ queryKey: ['depreciation-methods'], queryFn: () => depreciationMethods.list({ limit: 100 }), enabled: kind === 'DEPR_PARAMS' });
  const { all, candidates } = useAssetCandidates(orgEff, wanted, true);
  const list = candidates.filter((c) => c.kind === form.asset_kind);
  const cur = all.find((c) => c.a.id === form.asset_id);
  const [error, setError] = useState<string | null>(null);
  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`, change_kind: kind, asset_kind: form.asset_kind,
    fixed_asset_id: form.asset_kind === 'FIXED_ASSET' ? form.asset_id : null, intangible_asset_id: form.asset_kind === 'INTANGIBLE' ? form.asset_id : null,
    effective_date: form.effective_date, reason: form.reason || null,
    new_useful_life_months: kind === 'USEFUL_LIFE' ? Number(form.new_useful_life_months) || null : null,
    new_depreciation_method_id: kind === 'DEPR_PARAMS' ? (form.new_depreciation_method_id || null) : null,
    new_liquidation_value: kind === 'DEPR_PARAMS' ? (form.new_liquidation_value.trim() || null) : null, comment: form.comment || null,
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['asset-state-change'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); qc.invalidateQueries({ queryKey: ['asset-card'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => { const doc = isNew ? await assetStateChange.create(payload() as never) : await assetStateChange.update(id!, payload() as never); if (andPost) {
        try { await assetStateChange.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/state-change/s/${doc.id}`, { replace: true });
          throw e;
        }
      } return doc; },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/state-change/s/${doc.id}`, { replace: true }); }, onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => assetStateChange.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => assetStateChange.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/state-change'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && !!form.asset_id && (kind !== 'USEFUL_LIFE' || Number(form.new_useful_life_months) > 0)
    && (kind !== 'DEPR_PARAMS' || !!form.new_depreciation_method_id || !!form.new_liquidation_value.trim());
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  // Обовʼязкове у «Параметрах зміни» — як у canSave: новий строк (USEFUL_LIFE)
  // або хоча б один з параметрів амортизації (DEPR_PARAMS); інші види — без обовʼязкових.
  const paramsRequired = kind === 'USEFUL_LIFE' || kind === 'DEPR_PARAMS';
  const paramsOk = kind === 'USEFUL_LIFE' ? Number(form.new_useful_life_months) > 0
    : kind === 'DEPR_PARAMS' ? (!!form.new_depreciation_method_id || !!form.new_liquidation_value.trim())
    : true;
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) + (form.asset_id ? 1 : 0) },
    { id: 'params', title: 'Параметри зміни', required: paramsRequired ? 1 : 0,
      filled: paramsRequired && paramsOk ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{isNew ? `Зміна стану: ${CHANGE_KIND_UA[kind]}` : `Зміна стану ${d?.document_number ?? ''} — ${CHANGE_KIND_UA[kind]}`}{imported && <ImportedBadge source="1c" />}</h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar onBackToList={() => navigate('/assets/state-change')} onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew} onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          postTitle="Проведення змінює стан/строк картки; розпроведення повертає попередній"
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending} deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }} deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/state-change/s/${id}/print`)} entriesDoc={{ type: 'doc.asset_state_change', id: isNew ? '' : id! }}
          railMode extraActions={railActions} />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.asset_state_change', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={fs}>
        {/* 2026-08-31 (рішення власника): вид операції — одразу ЗА «Поточний стан»;
            актив — пошукове поле з урахуванням виду активу.
            ч.915: тому рядок активу разом із «Видом операції» (перемикач форм хаба)
            лишається в «Основних даних» — порядок полів не змінено. */}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}><label>Організація *</label><select value={orgEff} disabled={!isNew} onChange={(e) => setForm({ ...form, organization_id: e.target.value, asset_id: '' })}>{availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата документа *</label><DatePopover value={form.document_date} onChange={(v) => setForm({ ...form, document_date: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата зміни</label><DatePopover value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} /></div>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 2' }}><label>Вид активу</label><select value={form.asset_kind} disabled={!isNew} onChange={(e) => setForm({ ...form, asset_kind: e.target.value as 'FIXED_ASSET' | 'INTANGIBLE', asset_id: '' })}><option value="FIXED_ASSET">ОЗ</option><option value="INTANGIBLE">НМА</option></select></div>
          <div className="field" style={{ gridColumn: 'span 5' }}><label>Актив <span style={{ color: 'red' }}>*</span></label>
            {isNew ? (
              <SearchSelect
                items={(cur && !list.some((c) => c.a.id === cur.a.id) ? [cur, ...list] : list)
                  .map((c) => ({ id: c.a.id, label: `${c.a.name} (${ASSET_STATUS_UA[c.a.status]})`, hint: c.a.inventory_number }))}
                value={form.asset_id}
                onChange={(v) => setForm({ ...form, asset_id: v })}
                placeholder="пошук: назва або інв. № (показано припустимі для операції)"
              />
            ) : (
              <input disabled value={cur ? `${cur.a.inventory_number} ${cur.a.name}` : ''} />
            )}
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}><label>Поточний стан</label><input disabled value={cur ? ASSET_STATUS_UA[cur.a.status] : ''} /></div>
          <KindSelect kind={kind} setKind={setKind} disabled={!isNew} />
        </div>
      </fieldset>
      </DocBlock>

      {/* ч.915: нові значення (за видом операції) + підстава; «Було» — після проведення. */}
      <DocBlock index={2} title="Параметри зміни">
      <fieldset disabled={locked} style={fs}>
        {kind === 'USEFUL_LIFE' && (
          <div className="field" style={{ maxWidth: 280 }}><label>Новий строк, міс <span style={{ color: 'red' }}>*</span>{cur && <small style={{ color: 'var(--muted)' }}> (зараз {cur.a.useful_life_months})</small>}</label><input type="number" min="1" value={form.new_useful_life_months} onChange={(e) => setForm({ ...form, new_useful_life_months: e.target.value })} /></div>
        )}
        {kind === 'DEPR_PARAMS' && (
          <div className="field" style={{ maxWidth: 280 }}><label>Нова ліквід. вартість <small style={{ color: 'var(--muted)' }}>(порожньо — не змінювати)</small></label><input type="number" step="0.01" value={form.new_liquidation_value} onChange={(e) => setForm({ ...form, new_liquidation_value: e.target.value })} /></div>
        )}
        {kind === 'DEPR_PARAMS' && (
          <div className="field"><label>Новий метод амортизації <small style={{ color: 'var(--muted)' }}>(порожньо — не змінювати)</small></label>
            <select value={form.new_depreciation_method_id} onChange={(e) => setForm({ ...form, new_depreciation_method_id: e.target.value })}>
              <option value="">— не змінювати —</option>
              {(methodsQ2.data?.items ?? []).map((m) => <option key={m.id} value={m.id}>{m.name_uk}</option>)}
            </select></div>
        )}
        <div className="field"><label>Підстава (наказ, причина)</label><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
        {d?.posted && <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0 0 0.6rem' }}>Було: {kind === 'USEFUL_LIFE' ? `${d.prev_useful_life_months} міс` : kind === 'DEPR_PARAMS' ? `ліквід. ${d.prev_liquidation_value ?? '—'}` : ASSET_STATUS_UA[d.prev_status ?? ''] ?? d.prev_status}</p>}
      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={fs}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label><input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.asset_state_change"
            documentId={isNew ? undefined : id} />
          {/* ч.915: підказка нового документа — поруч із вкладеннями (текст без змін). */}
          {isNew && <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.4rem 0 0' }}>Вкладення (скани наказів) зʼявляться після збереження документа.</p>}
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}
